<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';

export default{
  name: "CreateCondoModal",
  props: {
    isEditMode: {
      type: Boolean,
      default: false
    },
    editObj: {
      type: Object
    }
  },
  data(){
    return {
      payload: {
        image: null,
        condo_name: '',
        address: '',
        description: '',
      },
      imagePreview: null,
      errMsg: {
        condo_name: '',
        address: '',
        description: ''
      }
    }
  },
  computed: {
    isFormValid(){
      return this.payload.condo_name.length > 0 
        && this.payload.address.length > 0 
        && this.payload.description.length > 0
    }
  },
  methods: {
    ...mapActions('condoMngt', ['createCondo', 'editCondo']),
    async handleConfirm(){
      this.errMsg = {
        condo_name: '',
        address: '',
        description: ''
      }
      if(!this.isFormValid){
        useAlert('Make sure all required fields are filled')
        if(this.payload.condo_name.length <= 0){
          this.errMsg.condo_name = 'This field is required'
        }
        if(this.payload.address.length <= 0){
          this.errMsg.address = 'This field is required'
        }
        if(this.payload.description.length <= 0){
          this.errMsg.description = 'This field is required'
        }
        return
      }
      try{
        if(!this.isEditMode){
          await this.createCondo(this.payload);
        }else{
          await this.editCondo({ 
            condoId: this.editObj.condo_id, 
            payload: {
              condo_name: this.payload.condo_name,
              address: this.payload.address,
              description: this.payload.description,
              ...(this.payload.image && { image: this.payload.image })
            }
          });
        }
        useAlert(`Condo ${this.isEditMode ? 'edited' : 'created'} successfully`)
        this.$emit('close')
        this.$emit('refresh')
      }catch(err){
        if (Array.isArray(err)) {
          useAlert(err.join('\n'))
        } else {
          useAlert(`Failed to ${this.isEditMode ? 'edit' : 'creat'} condo`)
          console.error(err);
        }
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.payload.image = file;
        this.imagePreview = URL.createObjectURL(file);
      } else {
        this.imagePreview = null;
      }
    },
    removeImage() {
      this.payload.image = null;
      this.imagePreview = null;
      this.$refs.fileInput.value = null;
    },
    handleDrop(e) {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) {
        this.payload.image = file;
        this.imagePreview = URL.createObjectURL(file);
      }
    }
  },
  mounted(){
    if(this.isEditMode){
      this.payload.condo_name = this.editObj.condo_name;
      this.payload.address = this.editObj.address;
      this.payload.description = this.editObj.description;
      if (this.editObj.presigned_image_url) {
        this.imagePreview = this.editObj.presigned_image_url;
      }
    }
  }
}
</script>

<template>
  <div class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50">
    <div class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-96 max-h-[80vh] overflow-y-auto">
      <!-- header -->
       <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">{{ isEditMode ? 'Edit' : 'Create' }} Condo</h1>
        <woot-button variant="clear" color-scheme="alert" icon="dismiss" @click="$emit('close')"/>
       </div>

      <form>
        <label class="block mb-2 text-slate-700 dark:text-slate-200">Upload an image</label>
        <div
          class="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer 
                hover:border-blue-500 dark:hover:border-blue-400 
                transition mb-4 bg-white dark:bg-slate-700/50 
                border-slate-300 dark:border-slate-600"
          @dragover.prevent
          @drop="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleFileChange"
          />

          <div v-if="imagePreview" class="relative">
            <img
              :src="imagePreview"
              alt="Image preview"
              class="rounded-lg border w-full h-48 object-cover 
                    border-slate-300 dark:border-slate-600"
            />
            <woot-button 
              size="small" 
              icon="delete" 
              color-scheme="alert" 
              @click.stop="removeImage" 
              class="absolute top-0 right-0 hover:bg-red-300 dark:hover:bg-red-500/70" 
            />
          </div>

          <div v-else class="text-slate-500 dark:text-slate-400">
            <p>Drag & drop an image here, or <span class="text-blue-600 dark:text-blue-400">browse</span></p>
            <p class="text-xs text-slate-400 dark:text-slate-500">PNG, JPG, JPEG — up to 5MB</p>
          </div>
        </div>

        <label for="condo_name" class="text-slate-700 dark:text-slate-300">Condo Name:</label>
        <input 
          v-model="payload.condo_name" 
          id="condo_name" 
          type="text" 
          placeholder="Enter a name..."
          :class="{
            'reset-base p-2 rounded w-full mb-0 border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500': true,
            'border-slate-200 dark:border-slate-700': !errMsg.condo_name,
            'border-red-500 focus:border-red-500': errMsg.condo_name
          }"
        />
        <p v-if="errMsg.condo_name" class="text-red-500">{{ errMsg.condo_name }}</p>

        <label for="address" class="text-slate-700 dark:text-slate-300">Address:</label>
        <input 
          v-model="payload.address" 
          id="address" 
          type="text" 
          placeholder="Enter an address..."
          :class="{
            'reset-base p-2 rounded w-full mb-0 border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500': true,
            'border-slate-200 dark:border-slate-700': !errMsg.address,
            'border-red-500 focus:border-red-500': errMsg.address
          }"
        />
        <p v-if="errMsg.address" class="text-red-500">{{ errMsg.address }}</p>

        <label for="description" class="text-slate-700 dark:text-slate-300">Description:</label>
        <textarea 
          v-model="payload.description" 
          id="description"
          placeholder="Enter an description..."
          :class="{
            'reset-base p-2 rounded w-full mb-0 border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500': true,
            'border-slate-200 dark:border-slate-700': !errMsg.description,
            'border-red-500 focus:border-red-500': errMsg.description
          }"
        ></textarea>
        <p v-if="errMsg.description" class="text-red-500 mb-0">{{ errMsg.description }}</p>
      </form>

      <!-- Action buttons -->
       <div class="flex justify-end gap-4 mt-4">
        <woot-button color-scheme="secondary" @click="$emit('close')">Cancel</woot-button>
        <woot-button @click="handleConfirm">Confirm</woot-button>
       </div>
    </div>
  </div>
</template>
